import { Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core'

import { APPLICATION_WARNING_LABELS } from '@modules/private/application-messages'

/**
 * Las cuatro operaciones del editor. Recalcular, Limpiar letras cubiertas y Ajustar totales a mano se retiraron el
 * 29-sep-2026 (nacieron para sanear la migración; Operaciones nunca las usó): el API ya no las acepta.
 */
type Operation = 'EDIT_PERIODS' | 'EARLY_TERMINATION' | 'WRITE_OFF_RESIDUAL' | 'WAIVE_MORA'

/** Umbral por omisión de «Condonar diferencias menores»: la tolerancia de saldo del sistema. */
const RESIDUAL_LIMIT = 10

/** IVA de la letra. Solo para mostrar la mensualidad de accesorios con IVA en la captura; el cálculo es del servidor. */
const IVA_RATE = 0.16
type Scope = 'SINGLE' | 'MULTI' | 'CASCADE'
type CascadeMode = 'SAME_VALUE' | 'SAME_DELTA'

/** Conceptos editables de una letra. `rent` NO se captura: se deriva de su suma. */
const CONCEPTS = ['capital', 'interest', 'accessoriesAmount', 'accessoriesInterest', 'inputIva'] as const

/**
 * Lo que se captura en modo renta: los dos montos que Operaciones conoce del pagaré. La renta sin IVA y la
 * mensualidad de accesorios (capital + comisión) tal como viene, que es exactamente la que queda en la letra.
 */
const RENT_FIELDS = ['rentAmount', 'accessoriesMonthly'] as const

type CaptureMode = 'CONCEPTS' | 'RENT'

/**
 * Editor de saldos del arrendamiento.
 *
 * Regla central del flujo: **no se puede guardar sin haber previsualizado**. El botón de aplicar
 * permanece deshabilitado hasta que exista una previsualización vigente, el motivo esté escrito y
 * todas las advertencias bloqueantes estén confirmadas. Cualquier cambio en la captura invalida la
 * previsualización y obliga a volver a calcularla.
 */
@Component({
  selector: 'app-edit-amounts-modal',
  templateUrl: './edit-amounts-modal.component.html',
  styleUrls: ['./edit-amounts-modal.component.scss'],
})
export class EditAmountsModalComponent implements OnChanges {
  @Input() visible = false
  /** Periodos del arrendamiento, ya ordenados por número de letra. */
  @Input() periods: any[] = []
  /** Saldos actuales del arrendamiento, para el encabezado de contexto. */
  @Input() totals: any = null
  @Input() leaseStatus = ''
  /** Tasa anual de la cotización. Solo se pinta: la derivación la hace el servidor. */
  @Input() interestRate: number | null = null
  @Input() previewing = false
  @Input() saving = false
  /** Abierto desde el bloque de mora de una letra: arranca en condonación con solo esa letra. */
  @Input() presetWaivePeriod: number | null = null

  //* Binding de DOS vías: con `[visible]` de una sola vía + `(onHide)`, el change detection
  //* re-empuja el valor viejo y el modal se reabre (bug real documentado en ui-styleguide.md §3.12).
  @Output() visibleChange = new EventEmitter<boolean>()
  @Output() onHide = new EventEmitter<void>()
  @Output() onPreview = new EventEmitter<any>()
  @Output() onApply = new EventEmitter<any>()

  readonly concepts = CONCEPTS
  readonly conceptLabels: Record<string, string> = {
    capital: 'Capital',
    interest: 'Interés',
    accessoriesAmount: 'Accesorios',
    accessoriesInterest: 'Com. Acc.',
    inputIva: 'IVA',
  }
  readonly rentFields = RENT_FIELDS
  get rentFieldLabels(): Record<string, string> {
    return {
      rentAmount: this.rentIncludesIva ? 'Renta (con IVA)' : 'Renta (sin IVA)',
      accessoriesMonthly: this.accessoriesIncludeIva ? 'Accesorios del mes (con IVA)' : 'Accesorios del mes (sin IVA)',
    }
  }
  readonly statusLabels: Record<string, string> = { PAID: 'PAGADO', OVERDUE: 'ATRASADO', PENDING: 'PENDIENTE', ANTICIPATED: 'ANTICIPADO' }

  //* El mismo diccionario que la aplicación de pagos: un cambio de estado no puede decirse de dos formas.
  //* Los avisos propios del editor (PAID_PERIODS, COVERED_LEFTOVER…) no están ahí y siguen solo con su mensaje.
  readonly warningLabels = APPLICATION_WARNING_LABELS
  readonly totalLabels: Record<string, string> = {
    walletValue: 'Valor de cartera',
    rentsToCollectAmount: 'Rentas por cobrar',
    rentsCollectedAmount: 'Rentas cobradas',
    rentsToCollect: 'Letras pendientes',
    rentsCollected: 'Letras cubiertas',
    walletValueWithoutAccessories: 'Cartera sin accesorios',
    total: 'Cartera total',
    moraDays: 'Días de mora',
    'components.rent': 'Componente · renta',
    'components.accessories': 'Componente · accesorios',
    'components.mora': 'Componente · mora',
    'components.residual': 'Componente · residual',
  }

  operation: Operation | null = null
  //* Por defecto se captura Renta y Accesorios: es lo que Operaciones tiene del pagaré. El modo por
  //* conceptos se conserva para la letra cuyo IVA o comisión estén mal por sí mismos.
  captureMode: CaptureMode = 'RENT'
  /** Si la mensualidad de accesorios que se teclea ya trae el IVA. Apagada: el servidor le suma el 16 %. */
  accessoriesIncludeIva = false
  /** B-032 · Lo mismo para la renta: el pagaré la trae con IVA. Apagada: el servidor le suma el 16 %. */
  rentIncludesIva = false
  scope: Scope = 'SINGLE'
  cascadeMode: CascadeMode = 'SAME_VALUE'
  fromPeriod: number | null = null
  /** Copias editables indexadas por número de letra. */
  edits: Record<string, any> = {}
  selectedPeriods: number[] = []
  reason = ''

  terminationDate: Date | null = null
  pendingHandling: 'WRITE_OFF' | 'KEEP_AS_RECEIVABLE' | null = null
  residualThreshold = RESIDUAL_LIMIT
  //* Una fila por letra con mora pendiente; `amount` en null significa condonar toda la pendiente.
  waiveRows: { period: number; amount: number | null; max: number }[] = []

  preview: any = null
  previewError = ''
  confirmations: Record<string, boolean> = {}

  /**
   * PrimeNG solo engancha Escape cuando `closeOnEscape && closable`, y aquí los dos están apagados
   * para que toda salida pase por la confirmación de descarte. Sin este handler Esc no hacía nada
   * —el comentario del template afirmaba lo contrario— y las únicas salidas eran la X y Cancelar.
   */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.visible) this.requestHide()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) this.reset()
  }

  private reset(): void {
    this.confirmingClose = false
    this.operation = null
    this.captureMode = 'RENT'
    this.accessoriesIncludeIva = false
    this.rentIncludesIva = false
    this.scope = 'SINGLE'
    this.cascadeMode = 'SAME_VALUE'
    this.fromPeriod = null
    this.edits = {}
    this.selectedPeriods = []
    this.reason = ''
    this.terminationDate = null
    this.pendingHandling = null
    this.residualThreshold = RESIDUAL_LIMIT
    //! Mora pendiente = devengada − condonada − COBRADA. Sin restar lo cobrado, en una letra con parte de
    //! la mora ya aplicada el modal ofrecería condonar de más y el rechazo llegaría después de previsualizar.
    const pendiente = (p: any) => Number(p.moraAmount || 0) - Number(p.moraWaived || 0) - Number(p.appliedAmounts?.moraAmount || 0)
    this.waiveRows = (this.periods || []).filter((p: any) => pendiente(p) > 0).map((p: any) => ({ period: p.period, amount: null, max: pendiente(p) }))
    //* Abierto desde la letra: arranca en condonación con esa letra ya elegida.
    if (this.presetWaivePeriod != null) {
      this.operation = 'WAIVE_MORA'
      this.waiveRows = this.waiveRows.filter((r) => r.period === this.presetWaivePeriod)
    }
    this.preview = null
    this.previewError = ''
    this.confirmations = {}
  }

  // ── Selección de operación ───────────────────────────────────────────────

  readonly operations: { key: Operation; icon: string; title: string; hint: string }[] = [
    {
      key: 'EDIT_PERIODS',
      icon: 'fa-solid fa-table-list',
      title: 'Editar letras',
      hint: 'Cambia la renta o los accesorios de una letra o de varias en cascada. Los saldos se recalculan desde la tabla.',
    },
    {
      key: 'WRITE_OFF_RESIDUAL',
      icon: 'fa-solid fa-broom',
      title: 'Condonar diferencias menores',
      hint: 'Perdona los pesos o centavos que dejan letras sin saldar, por debajo del umbral que elijas.',
    },
    {
      key: 'WAIVE_MORA',
      icon: 'fa-solid fa-hand-holding-dollar',
      title: 'Condonar mora',
      hint: 'Perdona la mora pendiente de una o varias letras. Una condonación vigente por letra; se deshace desde la Bitácora.',
    },
    { key: 'EARLY_TERMINATION', icon: 'fa-solid fa-flag-checkered', title: 'Terminación anticipada', hint: 'Saca el arrendamiento de la cartera. Decides qué pasa con el saldo pendiente.' },
  ]

  selectOperation(op: Operation): void {
    this.operation = op
    this.invalidatePreview()
  }

  /** Cubierta por saldo real, como la cartera: una anticipada con su accesorio por vencer todavía debe (C5). */
  isCovered(period: any): boolean {
    return Number(period.payment || 0) <= 0.01
  }

  // ── Captura ──────────────────────────────────────────────────────────────

  /** Letras dentro del alcance elegido. Es lo que se pinta en la rejilla editable. */
  get periodsInScope(): any[] {
    if (this.operation !== 'EDIT_PERIODS') return []
    if (this.scope === 'CASCADE') {
      const from = this.fromPeriod ?? this.periods[0]?.period
      return this.periods.filter((p) => p.period >= from)
    }
    if (this.scope === 'MULTI') return this.periods.filter((p) => this.selectedPeriods.includes(p.period))
    const first = this.selectedPeriods[0]
    return first != null ? this.periods.filter((p) => p.period === first) : []
  }

  /** En cascada solo se captura la primera letra; el resto la sigue según el modo. */
  get editableRows(): any[] {
    const scope = this.periodsInScope
    return this.scope === 'CASCADE' ? scope.slice(0, 1) : scope
  }

  /** Las columnas editables de la rejilla, que dependen de lo que el operador capture. */
  get capturedFields(): readonly string[] {
    return this.captureMode === 'RENT' ? RENT_FIELDS : CONCEPTS
  }

  /** Etiqueta de la columna según el modo: cada modo captura sus propios campos. */
  fieldLabel(field: string): string {
    const mapa = this.derivesOnServer ? this.rentFieldLabels : this.conceptLabels
    return mapa[field] ?? field
  }

  /**
   * En modo renta el desglose lo hace el servidor y se ve en la previsualización. Aquí NO se
   * replica la fórmula financiera: duplicarla la dejaría desincronizándose en silencio, que es
   * justo lo que el `dryRun` existe para evitar.
   */
  get derivesOnServer(): boolean {
    return this.captureMode === 'RENT'
  }

  /** La renta del activo de una letra: capital + interés. Es lo que se captura en modo renta. */
  rentAmountOf(period: any): number {
    return Math.round((Number(period.capital || 0) + Number(period.interest || 0)) * 100) / 100
  }

  /** La mensualidad de accesorios como la dice el pagaré: capital + comisión, sin IVA. */
  accessoriesMonthlyOf(period: any): number {
    return Math.round((Number(period.accessoriesAmount || 0) + Number(period.accessoriesInterest || 0)) * 100) / 100
  }

  /**
   * Lo que la letra tiene guardado hoy en ese campo, para saber si el operador lo movió. La mensualidad de
   * accesorios se precarga completa (capital + comisión), la cifra del pagaré. Renta y accesorios llevan su IVA si
   * su casilla lo pide.
   */
  private baselineOf(period: any, field: string): number {
    if (field === 'rentAmount') {
      const renta = this.rentAmountOf(period)
      return this.rentIncludesIva ? Math.round(renta * (1 + IVA_RATE) * 100) / 100 : renta
    }
    if (field === 'accessoriesMonthly') {
      const mensual = this.accessoriesMonthlyOf(period)
      return this.accessoriesIncludeIva ? Math.round(mensual * (1 + IVA_RATE) * 100) / 100 : mensual
    }
    return Number(period[field] || 0)
  }

  valueOf(period: any, concept: string): number {
    const draft = this.edits[period.period]
    if (draft?.[concept] !== undefined && draft?.[concept] !== null) return draft[concept]
    return this.baselineOf(period, concept)
  }

  setValue(period: any, concept: string, value: number | null): void {
    if (!this.edits[period.period]) this.edits[period.period] = {}
    this.edits[period.period][concept] = value ?? 0
    this.invalidatePreview()
  }

  onCaptureModeChange(): void {
    //* Lo capturado en un modo no significa lo mismo en el otro: se descarta en vez de traducirse.
    this.edits = {}
    this.invalidatePreview()
  }

  /** Con o sin IVA la misma cifra significa otra cosa: se descarta lo capturado y la precarga cambia. */
  onIncludeIvaChange(): void {
    this.edits = {}
    this.invalidatePreview()
  }

  /**
   * `rent` es el total contractual de la letra: la suma de sus conceptos. Se muestra en vivo.
   * Se redondea a dos decimales igual que el backend: sin eso, 20476.57 + 11716.42 + 5150.88 da
   * 37343.870000000006 y la letra se pintaría como modificada sin que nadie la tocara.
   */
  computedRent(period: any): number {
    return Math.round(CONCEPTS.reduce((sum, c) => sum + Number(this.valueOf(period, c) || 0), 0) * 100) / 100
  }

  /** Si el pago esperado cambió respecto del que tiene guardado la letra. */
  rentChanged(period: any): boolean {
    return !this.derivesOnServer && this.computedRent(period) !== Math.round(Number(period.rent || 0) * 100) / 100
  }

  hasChanges(period: any): boolean {
    return this.capturedFields.some((c) => Number(this.valueOf(period, c)) !== this.baselineOf(period, c))
  }

  togglePeriod(period: any): void {
    if (this.scope === 'SINGLE') {
      this.selectedPeriods = [period.period]
    } else {
      const i = this.selectedPeriods.indexOf(period.period)
      i >= 0 ? this.selectedPeriods.splice(i, 1) : this.selectedPeriods.push(period.period)
    }
    this.invalidatePreview()
  }

  isSelected(period: any): boolean {
    return this.selectedPeriods.includes(period.period)
  }

  onScopeChange(): void {
    this.selectedPeriods = []
    this.edits = {}
    this.invalidatePreview()
  }

  // ── Previsualización ─────────────────────────────────────────────────────

  /** Cualquier cambio en la captura tira la previsualización: el usuario debe recalcular. */
  invalidatePreview(): void {
    this.preview = null
    this.previewError = ''
    this.confirmations = {}
  }

  get canPreview(): boolean {
    if (!this.operation) return false
    switch (this.operation) {
      case 'EDIT_PERIODS':
        return this.editableRows.length > 0 && this.editableRows.some((p) => this.hasChanges(p))
      case 'EARLY_TERMINATION':
        return !!this.terminationDate && !!this.pendingHandling
      case 'WRITE_OFF_RESIDUAL':
        return this.residualThreshold > 0
      case 'WAIVE_MORA':
        return this.waiveRows.some((r) => r.amount === null || (r.amount > 0 && r.amount <= r.max))
    }
  }

  get blockingWarnings(): any[] {
    return (this.preview?.warnings || []).filter((w: any) => w.blocking)
  }

  /** El corte de una terminación anticipada: qué quedó pagado y qué queda sin pagar. */
  get terminationCut(): any {
    if (this.operation !== 'EARLY_TERMINATION') return null
    const w = (this.preview?.warnings || []).find((x: any) => x.code === 'EARLY_TERMINATION_SCOPE')
    return w?.detail?.pagado ? w.detail : null
  }

  /**
   * Qué significa que no haya pagos. "0 letras cubiertas · el cliente ya lo liquidó" era falso y
   * además tapaba la distinción que importa: un arrendamiento cuyas fechas de cobro todavía no
   * llegan no es lo mismo que uno en mora, aunque ambos tengan cero pagado.
   */
  paidSummary(cut: any): string {
    if (cut.pagado.letras > 0) return `${cut.pagado.letras} letra(s) cubierta(s) · el cliente ya lo liquidó`
    //* Una anticipada cobrada en el pago inicial deja dinero pagado sin ninguna letra completa: su
    //* accesorio sigue por vencer. Decir «sin pagos» junto a ese monto se contradecía.
    if (this.hasPaid(cut)) return 'Abonos sin ninguna letra completa · lo que resta de esas letras entra a la condonación'
    const vencidas = cut.pendiente?.vencidas?.letras ?? 0
    if (vencidas > 0) return `Sin pagos registrados, y ya hay ${vencidas} letra(s) vencida(s)`
    return 'Sin pagos registrados todavía · ninguna letra ha vencido'
  }

  /** Hay dinero pagado aunque no cubra ninguna letra completa (p. ej. la renta anticipada del pago inicial). */
  hasPaid(cut: any): boolean {
    return cut.pagado.letras > 0 || (cut.pagado.monto ?? 0) > 0.005
  }

  /** El arrendamiento aún no empieza a cobrarse: nada pagado y nada vencido. */
  notStarted(cut: any): boolean {
    return !this.hasPaid(cut) && (cut.pendiente?.vencidas?.letras ?? 0) === 0
  }

  get allWarningsConfirmed(): boolean {
    return this.blockingWarnings.every((w: any) => this.confirmations[w.code])
  }

  get canApply(): boolean {
    return !!this.preview && !!this.reason.trim() && this.allWarningsConfirmed && !this.saving
  }

  /**
   * Filas del diff que efectivamente cambian: las que no, no se pintan.
   *
   * !`wallet` dejó de ser plano: `components` es un objeto con los cuatro componentes de la cartera
   * !(F-074). Recorrer las claves a secas pintaba una fila «COMPONENTS 0 → [object Object]» con un
   * !delta `NaN` —y `NaN !== 0` es cierto, así que el filtro la dejaba pasar—. Se aplana en sus cuatro
   * !filas con nombre y se descarta cualquier valor que no sea un número.
   */
  get changedTotals(): { key: string; before: number; after: number; delta: number }[] {
    if (!this.preview) return []
    const plano = (obj: any, prefijo = ''): Record<string, number> => {
      const salida: Record<string, number> = {}
      for (const [k, v] of Object.entries(obj || {})) {
        if (v !== null && typeof v === 'object') Object.assign(salida, plano(v, `${prefijo}${k}.`))
        else if (typeof v === 'number' && !isNaN(v)) salida[`${prefijo}${k}`] = v
      }
      return salida
    }
    const antes = plano(this.preview.before)
    const despues = plano(this.preview.after)
    return Object.keys(despues)
      .map((k) => ({ key: k, before: antes[k] ?? 0, after: despues[k], delta: Math.round((despues[k] - (antes[k] ?? 0)) * 100) / 100 }))
      .filter((r) => r.delta !== 0)
  }

  private readonly diffLabels: Record<string, string> = { accessoriesMonthly: 'Accesorios del mes', rent: 'Pago esperado', payment: 'Por pagar', appliedPayment: 'Pagado', status: 'Estado' }

  /** La mensualidad de accesorios antes y después: la suma de sus dos conceptos, de lo que devolvió el servidor. */
  private monthlyCell(diff: any): { antes: number; despues: number } | null {
    const c = diff?.campos || {}
    if (!c.accessoriesAmount && !c.accessoriesInterest) return null
    const letra = this.periods.find((p) => p.period === diff.period) || {}
    const lado = (k: 'antes' | 'despues') =>
      Math.round(
        ((c.accessoriesAmount ? c.accessoriesAmount[k] : Number(letra.accessoriesAmount || 0)) + (c.accessoriesInterest ? c.accessoriesInterest[k] : Number(letra.accessoriesInterest || 0))) * 100,
      ) / 100
    return { antes: lado('antes'), despues: lado('despues') }
  }

  private readonly DIFF_ORDER = ['capital', 'interest', 'accessoriesAmount', 'accessoriesInterest', 'accessoriesMonthly', 'inputIva', 'rent', 'payment', 'appliedPayment', 'status']

  /**
   * Columnas del diff: la unión de los campos que cambiaron en alguna letra. Se pinta como tabla
   * de columnas fijas y no como una lista de pares — con 4 conceptos las parejas envolvían y la
   * fila se partía a media letra, que era ilegible.
   */
  get diffColumns(): { key: string; label: string; money: boolean }[] {
    const presentes = new Set<string>()
    for (const d of this.preview?.periodDiffs || []) Object.keys(d?.campos || {}).forEach((k) => presentes.add(k))
    //* Si cambió cualquier parte de los accesorios se pinta también la mensualidad, que es la cifra que tecleó el operador.
    if (presentes.has('accessoriesAmount') || presentes.has('accessoriesInterest')) presentes.add('accessoriesMonthly')
    return this.DIFF_ORDER.filter((k) => presentes.has(k)).map((k) => ({ key: k, label: this.conceptLabels[k] || this.diffLabels[k] || k, money: k !== 'status' }))
  }

  /** Celda del diff para una letra y un campo; `null` si esa letra no cambió en ese campo. */
  diffCell(diff: any, key: string): { antes: any; despues: any } | null {
    if (key === 'accessoriesMonthly') return this.monthlyCell(diff)
    const c = diff?.campos?.[key]
    if (!c) return null
    if (key !== 'status') return c
    return { antes: this.statusLabels[c.antes] || c.antes, despues: this.statusLabels[c.despues] || c.despues }
  }

  /** Los montos del arrendamiento se formatean como moneda; los conteos de letras no. */
  isMoney(key: string): boolean {
    //* `moraDays` y los conteos de letras NO son dinero; el resto de la cartera sí, componentes incluidos.
    if (key === 'moraDays' || key === 'rentsToCollect' || key === 'rentsCollected') return false
    return key.endsWith('Amount') || key.startsWith('wallet') || key === 'total' || key.startsWith('components.')
  }

  currentTotal(key: string): number {
    return this.totals?.[key] ?? 0
  }

  buildPayload(): any {
    const base: any = { operation: this.operation, reason: this.reason.trim() }
    if (this.preview) {
      base.stateHash = this.preview.stateHash
      base.confirmations = Object.keys(this.confirmations).filter((k) => this.confirmations[k])
    }

    if (this.operation === 'EDIT_PERIODS') {
      base.scope = this.scope
      if (this.scope === 'CASCADE') {
        base.cascadeMode = this.cascadeMode
        base.fromPeriod = this.fromPeriod ?? this.periods[0]?.period
      }
      base.captureMode = this.captureMode
      if (this.derivesOnServer && this.accessoriesIncludeIva) base.accessoriesIncludeIva = true
      if (this.derivesOnServer && this.rentIncludesIva) base.rentIncludesIva = true
      base.periods = this.editableRows
        .filter((p) => this.hasChanges(p))
        .map((p) => {
          const row: any = { period: p.period }
          //* En modo renta viaja solo lo que el operador movió: lo que no, la letra lo conserva tal cual. Mandar la
          //* mensualidad sin tocar la haría pasar por el reparto y, en cascada, la copiaría a todas las letras.
          const campos = this.derivesOnServer ? this.capturedFields.filter((c) => Number(this.valueOf(p, c)) !== this.baselineOf(p, c)) : this.capturedFields
          for (const c of campos) row[c] = Number(this.valueOf(p, c) || 0)
          return row
        })
    }

    if (this.operation === 'EARLY_TERMINATION') {
      const d = this.terminationDate as Date
      base.terminationDate = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString()
      base.pendingHandling = this.pendingHandling
    }

    if (this.operation === 'WRITE_OFF_RESIDUAL') base.residualThreshold = Number(this.residualThreshold)

    if (this.operation === 'WAIVE_MORA') {
      //* Se direcciona por número de letra, como el resto del editor. Sin monto se condona toda la pendiente.
      base.waivePeriods = this.waiveRows.filter((r) => r.amount === null || r.amount > 0).map((r) => (r.amount === null ? { period: r.period } : { period: r.period, amount: Number(r.amount) }))
    }

    return base
  }

  requestPreview(): void {
    this.previewError = ''
    this.onPreview.emit(this.buildPayload())
  }

  /** El padre entrega el resultado del dryRun. */
  setPreview(result: any, error?: string): void {
    if (error) {
      this.preview = null
      this.previewError = error
      return
    }
    this.preview = result
    this.confirmations = {}
  }

  apply(): void {
    if (!this.canApply) return
    this.onApply.emit(this.buildPayload())
  }

  hide(): void {
    this.visible = false
    this.visibleChange.emit(false)
    this.onHide.emit()
  }

  /**
   * Hay trabajo sin guardar, así que cerrar pide confirmación (excepción de wizard del styleguide).
   * La confirmación es **en línea**, no un `confirm()` nativo: un diálogo del navegador bloquea la
   * página entera y rompe la automatización, además de salirse del lenguaje visual del panel.
   */
  confirmingClose = false

  get isDirty(): boolean {
    return !!this.operation && (this.canPreview || !!this.preview || !!this.reason.trim())
  }

  requestHide(): void {
    if (this.isDirty && !this.confirmingClose) {
      this.confirmingClose = true
      return
    }
    this.hide()
  }

  cancelClose(): void {
    this.confirmingClose = false
  }
}
